/**
 * Class Registration Manager
 * Handles class selection, validation, and kit management for event registration
 * @module ClassRegistrationManager
 */

import { NotificationModal } from '@theme/notification-modal';
import { getScheduleRoom } from '@theme/schedule-room';
import { classesOverlap, getClassEndTime } from '@theme/class-time';

/**
 * @typedef {Object} ClassSelection
 * @property {string} class_id - Unique identifier for the class (GID format)
 * @property {string} class_name - Display name of the class
 * @property {string} date - Date of the class (e.g., "2024-03-15")
 * @property {string} time_slot - Time slot for the class (e.g., "Morning", "Afternoon")
 * @property {string|null} kit_variant_id - Associated kit variant ID if class has a kit
 * @property {string} registered_at - ISO timestamp when selection was made
 */

/**
 * @typedef {Object} KitData
 * @property {number} quantity - Number of kits (based on linked classes)
 * @property {string[]} linkedClasses - Array of class IDs linked to this kit
 * @property {string[]} linkedClassNames - Array of class names linked to this kit
 * @property {boolean} checked - Whether kit is selected for purchase
 * @property {string|null} title - Kit product title
 * @property {string|null} price - Kit price in cents
 * @property {string|null} priceFormatted - Formatted price string (e.g., "$49.99")
 */

/**
 * @typedef {Object} TicketConstraints
 * @property {number} classesLimit - Maximum classes allowed (-1 for unlimited, 0 for none)
 * @property {number} daysLimit - Maximum unique days allowed (-1 for unlimited, 0 for none)
 * @property {string|null} city - Normalized city name for the ticket
 */

/**
 * @typedef {Object} RegistrationState
 * @property {string|null} selectedTicketId - Currently selected ticket ID
 * @property {TicketConstraints} ticketConstraints - Constraints for the selected ticket
 * @property {Object<string, ClassSelection[]>} selections - Map of ticket ID to class selections
 * @property {Object<string, string[]>} timeSlotMap - Map of date-timeSlot to class IDs
 * @property {Object<string, KitData>} kits - Map of kit variant ID to kit data
 * @property {Object<string, string>} attendeeNames - Map of ticket ID to attendee name
 */

/**
 * @typedef {Object} RegistrationConfig
 * @property {boolean} [enforceTimeConflicts] - Whether to prevent overlapping class selections
 * @property {boolean} [enforceDayRestrictions] - Whether to enforce day limits
 */

/**
 * @typedef {Object} TicketRecord
 * @property {string} id - Ticket ID
 * @property {string} ticket_title - Display title of the ticket
 * @property {string} status - Ticket status (active, cancelled, refunded)
 * @property {string} [city] - City associated with the ticket
 * @property {number} [classes_limit] - Maximum classes allowed
 * @property {number} [days_limit] - Maximum days allowed
 * @property {Object} [constraints] - Nested constraints object
 * @property {number} [constraints.classes_limit] - Maximum classes allowed
 * @property {number} [constraints.days_limit] - Maximum days allowed
 */

/**
 * @typedef {Object} DOMCache
 * @property {HTMLElement|null} registrationRoot - Root registration container
 * @property {HTMLElement|null} summaryPanel - Summary panel element
 * @property {HTMLElement|null} emptyState - Empty state element
 * @property {HTMLElement|null} ticketsContainer - Container for ticket selections
 * @property {HTMLElement|null} kitsContainer - Container for kits
 * @property {HTMLElement|null} kitsList - List of kits
 * @property {HTMLElement|null} footer - Summary footer
 * @property {HTMLButtonElement|null} clearAllBtn - Clear all button
 * @property {HTMLButtonElement|null} submitBtn - Submit button
 * @property {NodeListOf<HTMLElement>|null} classCards - All class cards
 */

/**
 * @typedef {Object} RegistrationData
 * @property {string} ticket_id - Ticket ID for registration
 * @property {string|null} customer_id - Customer ID
 * @property {string|null} customer_email - Customer email
 * @property {string|null} city - Event city
 * @property {ClassSelection[]} classes - Array of class selections
 * @property {Object<string, KitData>} kits - Kit data
 * @property {string} registered_at - ISO timestamp
 * @property {'add'|'remove'} operation - Explicit schedule mutation
 */

/**
 * @typedef {Object} APIResponse
 * @property {boolean} success - Whether the operation succeeded
 * @property {string} [registration_id] - Generated registration ID
 * @property {string} [message] - Response message
 * @property {Object} [ticket] - Updated ticket data
 */

/**
 * @typedef {Object} CartItem
 * @property {string} id - Variant ID
 * @property {number} quantity - Quantity to add
 */

/**
 * HTML escapes a string to prevent XSS attacks
 * @param {string} str - String to escape
 * @returns {string} Escaped string safe for HTML insertion
 */
function escapeHTML(str) {
  if (str == null) return '';
  const div = document.createElement('div');
  div.appendChild(document.createTextNode(String(str)));
  return div.innerHTML;
}

/** Number of days before localStorage data is considered stale */
const STALE_DATA_DAYS = 7;

/**
 * Manages class registration flow including selection, validation, and kit management
 * @class
 */
class ClassRegistrationManager {
  /** @type {boolean} Debug flag for console logging */
  DEBUG = false;

  /** @type {RegistrationState} Current registration state */
  state;

  /** @type {RegistrationConfig|null} Configuration options */
  config;

  /** @type {TicketRecord[]|null} Available tickets */
  tickets;

  /** @type {string|null} Current customer ID */
  customerId;

  /** @type {string|null} Current customer email */
  customerEmail;

  /** @type {string|null} Normalized event city */
  eventCity;

  /** @type {DOMCache} Cached DOM elements */
  dom;

  /** @type {AbortController|null} Controller for aborting event listeners */
  abortController;

  /** @type {Object<string, Function>} Bound event handlers for element-specific cleanup */
  boundHandlers;

  /** @type {BroadcastChannel|null} Cross-tab registration refresh channel */
  scheduleChannel;

  /** @type {Set<string>} Tickets with attendee edits not yet committed to the server */
  dirtyAttendeeTicketIds;

  /** @type {number} Monotonic guard used to discard out-of-order server refreshes */
  syncRequestSequence;

  /**
   * Creates a new ClassRegistrationManager instance and initializes the registration UI
   */
  constructor() {
    this.log('Constructor called');

    this.state = {
      selectedTicketId: null,
      ticketConstraints: {},
      selections: {}, // { ticketId: [{ classId, date, timeSlot, kitVariantId }] }
      timeSlotMap: {}, // { 'date-timeSlot': [classIds] }
      kits: {}, // { kitVariantId: { quantity, linkedClasses: [] } }
      attendeeNames: {}, // { ticketId: 'John Doe' }
      summaryCollapsed: true, // Whether the summary panel is collapsed on mobile (collapsed by default)
      kitInfoCache: {}, // { kitVariantId: { title, price, priceFormatted } } - persistent cache of kit product info
    };

    this.config = null;
    this.tickets = null;
    this.customerId = null;
    this.customerEmail = null;
    this.eventCity = null;

    // AbortController for cleaning up document-level event listeners
    this.abortController = new AbortController();

    // Store bound handlers for element-specific listeners (buttons)
    this.boundHandlers = {};
    this.scheduleChannel = null;
    this.dirtyAttendeeTicketIds = new Set();
    this.syncRequestSequence = 0;

    // Cache frequently accessed DOM elements
    this.dom = {
      registrationRoot: null,
      summaryPanel: null,
      emptyState: null,
      ticketsContainer: null,
      kitsContainer: null,
      kitsList: null,
      footer: null,
      clearAllBtn: null,
      submitBtn: null,
      classCards: null,
    };

    this.log('Initializing ClassRegistrationManager');
    this.log('Calling init()');
    this.init();
  }

  /**
   * Logs messages to console when DEBUG is enabled
   * @param {...any} args - Arguments to log
   * @private
   */
  log(...args) {
    if (this.DEBUG) {
      console.log('[ClassRegistration]', ...args);
    }
  }

  /**
   * Sanitizes a GID to create a valid HTML ID attribute
   * Matches the Liquid template sanitization logic
   * @param {string} gid - Shopify GID (e.g., "gid://shopify/Product/123")
   * @returns {string} Sanitized ID safe for HTML attributes, or empty string if invalid
   */
  sanitizeId(gid) {
    if (typeof gid !== 'string') return '';
    // Replace :// first, then replace all remaining / with -
    return gid.replace('://', '-').replaceAll('/', '-');
  }

  /**
   * Normalizes a constraint value to a number or null
   * @param {*} value - Value to normalize
   * @returns {number|null} Normalized number or null if invalid
   * @private
   */
  normalizeConstraintValue(value) {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  /**
   * Gets a constraint value from a ticket record, checking both direct and nested constraints
   * @param {TicketRecord|null} ticketRecord - Ticket record to check
   * @param {string} key - Constraint key (e.g., "classes_limit", "days_limit")
   * @param {number} [fallback=-1] - Fallback value if not found
   * @returns {number} Constraint value or fallback
   * @private
   */
  getConstraintValue(ticketRecord, key, fallback = -1) {
    if (!ticketRecord) {
      return fallback;
    }

    const directValue = this.normalizeConstraintValue(ticketRecord[key]);
    if (directValue !== null) {
      return directValue;
    }

    if (ticketRecord.constraints) {
      const nestedValue = this.normalizeConstraintValue(ticketRecord.constraints[key]);
      if (nestedValue !== null) {
        return nestedValue;
      }
    }

    return fallback;
  }

  /**
   * Normalizes a city name to lowercase for comparison
   * @param {string|null|undefined} city - City name to normalize
   * @returns {string|null} Normalized city name or null
   * @private
   */
  normalizeCity(city) {
    if (!city) return null;
    return String(city).trim().toLowerCase();
  }

  /**
   * Resets the ticket selector dropdown to a specific value
   * @param {string|null} [value] - Value to set, defaults to current selected ticket
   * @private
   */
  resetTicketSelector(value = this.state.selectedTicketId) {
    const ticketSelector = document.querySelector('#ticket-select');
    if (ticketSelector) {
      ticketSelector.value = value || '';
    }

    if (!value) {
      this.setTicketSelectedState(false);
    }
  }

  /**
   * Initializes the registration manager
   * Order matters: localStorage loads first, then URL param takes precedence if different
   * @private
   */
  init() {
    // Capture URL param FIRST before any events can modify it
    this.initialUrlTicketId = this.getUrlTicketParam();

    this.loadConfiguration();
    this.bindEvents();
    this.initializeUI();
    this.loadFromLocalStorage(); // Restores previous state including selectedTicketId
    this.scanClassCardsForKitInfo(); // Build kit info cache from class cards in DOM
    this.selectTicketFromUrlParam(); // URL param takes precedence, skips if same ticket
    this.adoptTicketAlreadyInDropdown(); // Last resort: the dropdown shows one, we never heard
  }

  /**
   * Adopts a ticket the dropdown already shows but this manager never heard about.
   *
   * The ticket selector is a classic script that runs during parsing, so its
   * auto-select for a single ticket dispatches `ticket:selected` before this
   * deferred module has bound its listener — the event is lost, and re-picking that
   * same option fires no `change`, leaving the page with a ticket visibly chosen and
   * the classes hidden behind [data-ticket-selected="false"]. Browser form
   * restoration on reload lands in the same state. localStorage usually papered over
   * it, which is why it looked intermittent.
   *
   * Dispatches `change` rather than `ticket:selected` so the selector's own handler
   * runs too and the attendee field appears, exactly as for a manual pick.
   * @private
   */
  adoptTicketAlreadyInDropdown() {
    if (this.state.selectedTicketId) return;

    const ticketSelector = document.querySelector('#ticket-select');
    const ticketId = ticketSelector?.value;
    if (!ticketId) return;

    // Never resurrect a ticket selectTicketFromUrlParam has just rejected.
    if (!this.findTicketById(ticketId)) {
      this.log('Ticket in dropdown is not one of ours, ignoring:', ticketId);
      return;
    }

    this.log('Adopting ticket already shown in dropdown:', ticketId);
    ticketSelector.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Scans all class-card-component elements on the page and caches their kit info.
   * This ensures kit info is available even when rendering registered classes
   * that don't have kit_title in server response.
   * @private
   */
  scanClassCardsForKitInfo() {
    const classCards = document.querySelectorAll('class-card-component[data-kit-variant-id]');
    this.log('Scanning class cards for kit info, found:', classCards.length);

    classCards.forEach((card) => {
      const kitVariantId = card.dataset.kitVariantId;
      const kitTitle = card.dataset.kitProductTitle;
      const kitPrice = card.dataset.kitProductPrice;
      const kitPriceFormatted = card.dataset.kitProductPriceFormatted;

      // Only cache if we have a valid title (not empty)
      if (kitVariantId && kitTitle) {
        this.state.kitInfoCache[kitVariantId] = {
          title: kitTitle,
          price: kitPrice || null,
          priceFormatted: kitPriceFormatted || null,
        };
        this.log('Cached kit info from class card:', kitVariantId, this.state.kitInfoCache[kitVariantId]);
      }
    });

    this.log('Kit info cache after scan:', this.state.kitInfoCache);
  }

  /**
   * Auto-selects a ticket if ticket ID is present in URL params
   * Called after loadFromLocalStorage, so state.selectedTicketId may already be set
   * Uses initialUrlTicketId captured at start of init() to avoid race condition
   * where loadFromLocalStorage overwrites the URL param before we can read it
   * @private
   */
  selectTicketFromUrlParam() {
    const ticketIdFromUrl = this.initialUrlTicketId;
    if (!ticketIdFromUrl) return;

    // Skip if already selected (loadFromLocalStorage runs first, so this handles the case
    // where localStorage and URL have the same ticket - no need to re-select)
    if (this.state.selectedTicketId === ticketIdFromUrl) {
      this.log('Ticket from URL already selected:', ticketIdFromUrl);
      return;
    }

    this.log('Found ticket ID in URL:', ticketIdFromUrl);

    // Find the ticket in available tickets
    const ticketRecord = this.findTicketById(ticketIdFromUrl);
    if (!ticketRecord) {
      this.log('Ticket from URL not found in available tickets');
      // Clear invalid URL param
      this.updateUrlTicketParam(null);
      return;
    }

    // Set the ticket selector dropdown value
    const ticketSelector = document.querySelector('#ticket-select');
    if (ticketSelector) {
      ticketSelector.value = ticketIdFromUrl;

      // Get the selected option data
      const selectedOption = ticketSelector.options[ticketSelector.selectedIndex];
      if (selectedOption && selectedOption.value) {
        const classesLimit = parseInt(selectedOption.dataset.classesLimit, 10);
        const daysLimit = parseInt(selectedOption.dataset.daysLimit, 10);

        // Dispatch ticket:selected event (same as user selecting)
        // This allows the ticket selector script to update attendee field etc.
        document.dispatchEvent(
          new CustomEvent('ticket:selected', {
            detail: {
              ticketId: ticketIdFromUrl,
              classesLimit: classesLimit,
              daysLimit: daysLimit,
              city: selectedOption.dataset.ticketCity,
              skipConfirmation: true,
            },
          })
        );
      }
    }
  }

  /**
   * Loads configuration from the DOM data attribute
   * Parses tickets, customer info, and event settings
   * @private
   */
  loadConfiguration() {
    const configElement = document.querySelector('[data-registration-config]');
    if (!configElement) {
      console.error('Registration configuration not found');
      return;
    }

    try {
      const data = JSON.parse(configElement.textContent);
      this.config = data.config || {};
      this.tickets = (data.tickets?.tickets || []).map(ticket => ({
        ...ticket,
        selected_classes: (ticket.selected_classes || []).map(cls => ({ ...cls, room: null })),
      }));
      this.customerId = data.customerId || null;
      this.customerEmail = data.customerEmail || null;
      this.eventCity = data.eventCity ? String(data.eventCity).trim().toLowerCase() : null;

      this.log('Registration configuration loaded');

      if (data.eligible === false) {
        console.warn('User not eligible for registration');
      }

      // Validate against server data in background
      this.validateAndSyncWithServer();
    } catch (error) {
      console.error('Failed to parse registration configuration:', error);
    }
  }

  /**
   * Finds a ticket by ID, supporting both 'id' and 'ticket_id' field names
   * @param {string} ticketId - The ticket ID to find
   * @returns {Object|undefined} The ticket object or undefined if not found
   * @private
   */
  findTicketById(ticketId) {
    return this.tickets?.find(t => (t.id || t.ticket_id) === ticketId);
  }

  /**
   * Fetches authoritative registration data from server and syncs local state
   * Used for initial validation and on-demand refresh
   * @returns {Promise<boolean>} True if sync was successful
   */
  async syncWithServer() {
    const subpath = window.REGISTRATION_DEV ? 'registration-dev' : 'registration';
    const apiEndpoint = `/apps/${subpath}/data/registrations`;
    const requestSequence = ++this.syncRequestSequence;

    try {
      const response = await fetch(apiEndpoint, {
        method: 'GET',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
      });

      if (requestSequence !== this.syncRequestSequence) {
        this.log('Discarding stale server sync response:', requestSequence);
        return false;
      }

      if (!response.ok) {
        if (response.status === 401) {
          this.log('User not authenticated, skipping server sync');
          return false;
        }
        throw new Error(`Server returned ${response.status}`);
      }

      const result = await response.json();
      if (requestSequence !== this.syncRequestSequence) {
        this.log('Discarding stale server sync response:', requestSequence);
        return false;
      }
      if (!result.success || !result.data?.tickets) {
        this.log('Invalid response from server:', result);
        return false;
      }

      // Replace the complete committed ticket collection from server data.
      // Preserve boot-only presentation fields that the registrations endpoint
      // does not return, but never preserve committed registration fields.
      const serverTickets = result.data.tickets;
      const localTicketsById = new Map(
        this.tickets.map((ticket) => [ticket.id || ticket.ticket_id, ticket])
      );
      this.tickets = serverTickets.map((serverTicket) => {
        const localTicket = localTicketsById.get(serverTicket.ticket_id) || {};
        const hasUnsavedAttendee = this.dirtyAttendeeTicketIds.has(serverTicket.ticket_id);
        const attendeeName = hasUnsavedAttendee
          ? this.state.attendeeNames[serverTicket.ticket_id] || ''
          : serverTicket.attendee_name || '';
        const syncedTicket = {
          ...localTicket,
          id: localTicket.id || serverTicket.ticket_id,
          ticket_id: serverTicket.ticket_id,
          ticket_title: serverTicket.ticket_title,
          city: serverTicket.city,
          // The endpoint contains active tickets only. Explicitly retain that
          // invariant for tickets that were not present in Liquid boot data.
          status: serverTicket.status || 'active',
          attendee_name: attendeeName,
          constraints: serverTicket.constraints || {},
          selected_classes: serverTicket.classes || [],
          kits: serverTicket.kits || {},
          metadata: serverTicket.metadata || {},
        };
        if (!hasUnsavedAttendee) {
          this.state.attendeeNames[serverTicket.ticket_id] = attendeeName;
        }
        this.log('Synced ticket from server:', serverTicket.ticket_id);
        return syncedTicket;
      });

      // Dispatch event to update overview
      document.dispatchEvent(new CustomEvent('registration:synced', {
        detail: { tickets: this.tickets },
      }));

      // Greyed-out slots and limit messaging were computed from the Liquid boot
      // data, which can be stale. Re-derive them from the fresh server schedule.
      // Runs after the dispatch so card components can't re-enable buttons after it.
      this.updateAvailability();
      this.updateCTAs();

      this.log('Server sync complete');
      return true;
    } catch (error) {
      this.log('Server sync failed (will use metafield data):', error.message);
      return false;
    }
  }

  /**
   * Validates metafield data against server and logs discrepancies
   * Non-blocking - runs in background after page load
   * @private
   */
  async validateAndSyncWithServer() {
    await this.syncWithServer();
  }

  /**
   * Refreshes committed registrations and redraws consumers from server state.
   * Pending selections stay local and are never submitted by this refresh.
   * @returns {Promise<boolean>}
   * @private
   */
  async refreshCommittedSchedule() {
    return this.syncWithServer();
  }

  /** Notifies sibling tabs that canonical registration state changed. */
  notifyScheduleChanged(ticketId) {
    const message = { ticketId, timestamp: Date.now() };
    if (this.scheduleChannel) {
      this.scheduleChannel.postMessage(message);
    } else {
      try {
        localStorage.setItem('class_registration_schedule_changed', JSON.stringify(message));
      } catch (error) {
        this.log('Unable to publish registration storage event:', error);
      }
    }
  }

  /**
   * Saves temporary selection state to localStorage for sidebar restoration
   * This data is cleared after successful registration
   * @private
   */
  saveToLocalStorage() {
    try {
      const tempData = {
        ticketId: this.state.selectedTicketId,
        selections: this.state.selections,
        kits: this.state.kits,
        attendeeNames: this.state.attendeeNames,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem('class_registration_temp', JSON.stringify(tempData));
      this.log('Saved temp state to localStorage:', tempData);

      // Note: registration:updated event is for potential future use
      // The overview table ([data-registration-overview]) should NOT listen to this
      // as it should only reflect saved/committed state, not temporary selections
      document.dispatchEvent(new CustomEvent('registration:updated', {
        detail: tempData
      }));
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
    }
  }

  /**
   * Updates registration overview panel from API response data
   * Dispatches registration:updated event for overview table refresh
   * @param {APIResponse} response - API response containing ticket data
   * @param {boolean} [dispatchUpdate=true] - Whether to render before the full server refresh
   * @private
   */
  updateOverviewFromResponse(response, dispatchUpdate = true) {
    if (!response || !response.data) {
      this.log('No data in response, skipping overview update');
      return;
    }

    // Support both new structure (data.ticket) and old structure (data.ticket_id + data.classes)
    const ticketData = response.data.ticket || response.data;
    const ticketId = ticketData.ticket_id;
    const newClasses = ticketData.classes || [];

    // Update this.tickets to keep state in sync (single source of truth)
    const ticketRecord = this.findTicketById(ticketId);
    if (ticketRecord) {
      ticketRecord.selected_classes = newClasses;
      // Also update ticket metadata if provided in new response format
      if (response.data.ticket) {
        if (ticketData.ticket_title) ticketRecord.ticket_title = ticketData.ticket_title;
        if (ticketData.attendee_name) ticketRecord.attendee_name = ticketData.attendee_name;
        if (ticketData.constraints) ticketRecord.constraints = ticketData.constraints;
      }
      this.log('Updated this.tickets with new classes:', newClasses);
    } else {
      this.log('Ticket not found in this.tickets, cannot update state');
    }

    const overviewPanel = document.querySelector('[data-registration-overview]');
    if (!overviewPanel) {
      this.log('Overview panel not found');
      return;
    }

    // Transform backend response to match overview's expected format
    // Include full ticket data so overview can access kit_purchased flags
    const ticket = {
      id: ticketId,
      selected_classes: newClasses,
      // Pass through additional ticket data if available
      ...(response.data.ticket && {
        ticket_title: ticketData.ticket_title,
        attendee_name: ticketData.attendee_name,
        constraints: ticketData.constraints,
      }),
    };

    if (dispatchUpdate) {
      // Dispatch event to update overview with fresh data
      const event = new CustomEvent('registration:updated', {
        detail: { ticket },
      });
      document.dispatchEvent(event);
      this.log('Dispatched registration:updated event with response data:', ticket);
    }
  }

  /**
   * Loads temporary selection state from localStorage for sidebar restoration
   * Validates data freshness (expires after 7 days) and restores UI state
   * @private
   */
  loadFromLocalStorage() {
    try {
      const savedData = localStorage.getItem('class_registration_temp');
      if (!savedData) {
        this.log('No temp state in localStorage');
        return;
      }

      const tempData = JSON.parse(savedData);
      this.log('Loaded temp state from localStorage:', tempData);

      // Check if data is stale
      if (tempData.timestamp) {
        const savedDate = new Date(tempData.timestamp);
        const now = new Date();
        const daysSinceLastSave = (now - savedDate) / (1000 * 60 * 60 * 24);

        if (daysSinceLastSave > STALE_DATA_DAYS) {
          this.log(`Temp state is stale (>${STALE_DATA_DAYS} days), clearing`);
          localStorage.removeItem('class_registration_temp');
          return;
        }
      }

      // Restore temporary state (sidebar selections)
      if (tempData.ticketId) {
        this.state.selectedTicketId = tempData.ticketId;
      }
      if (tempData.selections) {
        this.state.selections = tempData.selections;
      }

      // Restore kits from localStorage if present
      // This preserves kit selection state (checked/unchecked) across page refreshes
      if (tempData.kits && Object.keys(tempData.kits).length > 0) {
        this.log('Restoring kits from localStorage - raw tempData.kits:', JSON.stringify(tempData.kits, null, 2));
        // A kit saved before online kit sales closed must not come back
        this.state.kits = Object.fromEntries(
          Object.entries(tempData.kits)
            .filter(([kitId]) => this.isKitAvailable(kitId))
            .map(([kitId, kitData]) => [
              kitId,
              {
                ...kitData,
                linkedClassNames: kitData.linkedClassNames || [],
              },
            ])
        );
        this.log('Restored kits from localStorage:', JSON.stringify(this.state.kits, null, 2));
      }

      // Restore attendee names from localStorage if present
      if (tempData.attendeeNames) {
        this.state.attendeeNames = tempData.attendeeNames;
        this.log('Restored attendee names from localStorage:', this.state.attendeeNames);
      }

      // Restore ticket constraints if ticket is selected
      if (tempData.ticketId) {
        const ticketRecord = this.findTicketById(tempData.ticketId);
        if (ticketRecord) {
          this.state.ticketConstraints = {
            classesLimit: this.getConstraintValue(ticketRecord, 'classes_limit', -1),
            daysLimit: this.getConstraintValue(ticketRecord, 'days_limit', -1),
            city: this.normalizeCity(ticketRecord.city),
          };

          // Trigger ticket selector to show the selected ticket and dispatch event
          const ticketSelector = document.querySelector('#ticket-select');
          if (ticketSelector) {
            ticketSelector.value = tempData.ticketId;

            // Manually trigger change event to update info panel
            const changeEvent = new Event('change', { bubbles: true });
            ticketSelector.dispatchEvent(changeEvent);
          }
        }
      }

      // Update UI to reflect restored state
      this.updateUIFromState();
      this.updateAvailability();
      this.updateSummary();
    } catch (error) {
      console.error('Failed to load from localStorage:', error);
    }
  }

  /**
   * Binds all event listeners for registration interactions
   * Handles ticket selection, class toggles, kit management, and form submission
   * Uses AbortController signal for document-level listeners to enable cleanup
   * @private
   */
  bindEvents() {
    const { signal } = this.abortController;

    window.addEventListener('focus', () => {
      void this.refreshCommittedSchedule();
    }, { signal });

    if ('BroadcastChannel' in window) {
      this.scheduleChannel = new BroadcastChannel('class-registration-schedule');
      this.scheduleChannel.addEventListener('message', () => {
        void this.refreshCommittedSchedule();
      });
    } else {
      window.addEventListener('storage', (event) => {
        if (event.key === 'class_registration_schedule_changed') {
          void this.refreshCommittedSchedule();
        }
      }, { signal });
    }

    // Ticket selection
    document.addEventListener('ticket:selected', (event) => {
      this.handleTicketSelect(event.detail);
    }, { signal });

    // Ticket deselection (when "Choose a ticket..." is selected)
    document.addEventListener('ticket:deselected', async () => {
      // Check for pending selections before deselecting
      if (this.hasPendingSelections()) {
        const { confirmed } = await NotificationModal.confirm(
          'You have unsaved class selections. Discard changes?',
          {
            confirmText: 'Discard',
            cancelText: 'Stay',
            type: 'warning',
            showLoading: false
          }
        );

        if (!confirmed) {
          // User chose to stay - reset the dropdown to current ticket
          this.resetTicketSelector(this.state.selectedTicketId);
          return;
        }
      }

      // Clear temporary state (full reset)
      this.clearTemporaryState(false);
      this.updateSummary();
    }, { signal });

    // Register button clicks
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-class-register-button]')) {
        this.log('Register button clicked');
        event.preventDefault(); // Prevent default button behavior
        event.stopPropagation(); // Prevent event from bubbling up
        const button = event.target.closest('[data-class-register-button]');
        const checkboxId = button.dataset.classCheckboxId;
        this.log('Looking for checkbox:', checkboxId);
        const checkbox = document.getElementById(checkboxId);
        if (checkbox) {
          const wasChecked = checkbox.checked;
          checkbox.checked = !checkbox.checked;
          this.log('Checkbox toggled:', wasChecked, '->', checkbox.checked);
          // Trigger change event to update registration state
          const changeEvent = new Event('change', { bubbles: true });
          checkbox.dispatchEvent(changeEvent);
        } else {
          this.log('Checkbox not found:', checkboxId);
        }
      }
    }, { signal });

    // Class checkbox changes
    document.addEventListener('change', (event) => {
      if (event.target.matches('[data-class-checkbox]')) {
        this.log('Checkbox changed', {
          id: event.target.id,
          checked: event.target.checked,
          classId: event.target.dataset.originalId
        });
        this.handleClassToggle(event.target);
      }
    }, { signal });

    // Kit checkbox toggles in summary panel
    document.addEventListener('change', (event) => {
      if (event.target.matches('[data-kit-checkbox-toggle]')) {
        const kitVariantId = event.target.dataset.kitVariantId;
        if (kitVariantId) {
          this.toggleKitChecked(kitVariantId);
        }
      }
    }, { signal });

    // Clear all button - store bound handler for cleanup
    const clearAllBtn = document.querySelector('[data-clear-all]');
    if (clearAllBtn) {
      this.boundHandlers.clearAll = () => this.clearAllSelections();
      clearAllBtn.addEventListener('click', this.boundHandlers.clearAll);
    }

    // Submit registration - Classes only
    const submitClassesOnlyBtn = document.querySelector('[data-submit-registration-classes-only]');
    if (submitClassesOnlyBtn) {
      this.boundHandlers.submitClassesOnly = () => this.submitRegistration(false);
      submitClassesOnlyBtn.addEventListener('click', this.boundHandlers.submitClassesOnly);
    }

    // Submit registration - With kits
    const submitWithKitsBtn = document.querySelector('[data-submit-registration-with-kits]');
    if (submitWithKitsBtn) {
      this.boundHandlers.submitWithKits = () => this.submitRegistration(true);
      submitWithKitsBtn.addEventListener('click', this.boundHandlers.submitWithKits);
    }

    // Expand/collapse summary panel
    const expandBtn = document.querySelector('[data-expand-btn]');
    if (expandBtn) {
      this.boundHandlers.expand = () => this.setSummaryCollapsed(false);
      expandBtn.addEventListener('click', this.boundHandlers.expand);
    }

    const collapseBtn = document.querySelector('[data-collapse-btn]');
    if (collapseBtn) {
      this.boundHandlers.collapse = () => this.setSummaryCollapsed(true);
      collapseBtn.addEventListener('click', this.boundHandlers.collapse);
    }

    // Class removal from summary
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-remove-class]')) {
        const classId = event.target.closest('[data-remove-class]').dataset.removeClass;
        this.removeClassSelection(classId);
      }
    }, { signal });

    // Kit removal from summary
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-remove-kit]')) {
        const kitVariantId = event.target.closest('[data-remove-kit]').dataset.removeKit;
        this.removeKit(kitVariantId);
      }
    }, { signal });
  }

  /**
   * Initializes the UI by caching DOM elements and setting initial state
   * @private
   */
  initializeUI() {
    // Cache frequently accessed DOM elements
    this.dom.summaryPanel = document.querySelector('[data-registration-summary]');
    this.dom.summaryContainer = document.querySelector('[data-summary-container]');
    this.dom.collapsedBar = document.querySelector('[data-collapsed-bar]');
    this.dom.collapsedCount = document.querySelector('[data-collapsed-count]');
    this.dom.expandBtn = document.querySelector('[data-expand-btn]');
    this.dom.collapseBtn = document.querySelector('[data-collapse-btn]');
    this.dom.emptyState = document.querySelector('[data-empty-state]');
    this.dom.ticketsContainer = document.querySelector('[data-tickets-container]');
    this.dom.kitsContainer = document.querySelector('[data-kits-container]');
    this.dom.kitsList = document.querySelector('[data-kits-list]');
    this.dom.footer = document.querySelector('[data-summary-footer]');
    this.dom.clearAllBtn = document.querySelector('[data-clear-all]');
    this.dom.submitBtn = document.querySelector('[data-submit-registration]');
    this.dom.classCards = document.querySelectorAll('[data-class-id]');
    this.dom.registrationRoot = document.querySelector('[data-class-registration]');

    this.setTicketSelectedState(false);

    // Initialize summary panel as hidden
    if (this.dom.summaryPanel) {
      this.dom.summaryPanel.hidden = true;
    }
  }

  /**
   * Cleans up event listeners and resources to prevent memory leaks
   * Call this method before removing the manager instance
   */
  destroy() {
    this.log('Destroying ClassRegistrationManager');

    // Abort all document-level event listeners
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    this.scheduleChannel?.close();
    this.scheduleChannel = null;

    // Remove element-specific listeners using stored bound handlers
    const clearAllBtn = document.querySelector('[data-clear-all]');
    if (clearAllBtn && this.boundHandlers.clearAll) {
      clearAllBtn.removeEventListener('click', this.boundHandlers.clearAll);
    }

    const submitClassesOnlyBtn = document.querySelector('[data-submit-registration-classes-only]');
    if (submitClassesOnlyBtn && this.boundHandlers.submitClassesOnly) {
      submitClassesOnlyBtn.removeEventListener('click', this.boundHandlers.submitClassesOnly);
    }

    const submitWithKitsBtn = document.querySelector('[data-submit-registration-with-kits]');
    if (submitWithKitsBtn && this.boundHandlers.submitWithKits) {
      submitWithKitsBtn.removeEventListener('click', this.boundHandlers.submitWithKits);
    }

    const expandBtn = document.querySelector('[data-expand-btn]');
    if (expandBtn && this.boundHandlers.expand) {
      expandBtn.removeEventListener('click', this.boundHandlers.expand);
    }

    const collapseBtn = document.querySelector('[data-collapse-btn]');
    if (collapseBtn && this.boundHandlers.collapse) {
      collapseBtn.removeEventListener('click', this.boundHandlers.collapse);
    }

    // Clear bound handlers
    this.boundHandlers = {};

    // Clear DOM cache
    this.dom = {};

    // Clear state
    this.state = null;
    this.config = null;
    this.tickets = null;

    this.log('ClassRegistrationManager destroyed');
  }

  /**
   * Sets the ticket selected state on the root element for CSS styling
   * @param {boolean} isSelected - Whether a ticket is selected
   * @private
   */
  setTicketSelectedState(isSelected) {
    if (!this.dom.registrationRoot) {
      this.dom.registrationRoot = document.querySelector('[data-class-registration]');
    }
    if (this.dom.registrationRoot) {
      this.dom.registrationRoot.dataset.ticketSelected = isSelected ? 'true' : 'false';
    }
  }

  /**
   * Checks if a ticket has pending (unsaved) class selections
   * @param {string} [ticketId] - Ticket ID to check, defaults to currently selected ticket
   * @returns {boolean} True if there are pending selections
   */
  hasPendingSelections(ticketId = this.state.selectedTicketId) {
    if (!ticketId) return false;
    const selections = this.state.selections[ticketId] || [];
    return selections.length > 0;
  }

  /**
   * Updates the URL with the current ticket ID parameter
   * @param {string|null} ticketId - Ticket ID to set, or null to remove
   * @private
   */
  updateUrlTicketParam(ticketId) {
    try {
      const url = new URL(window.location.href);
      if (ticketId) {
        url.searchParams.set('ticket', ticketId);
      } else {
        url.searchParams.delete('ticket');
      }
      window.history.replaceState({}, '', url.toString());
    } catch (error) {
      this.log('Failed to update URL param:', error);
    }
  }

  /**
   * Gets the ticket ID from URL parameters
   * @returns {string|null} Ticket ID from URL or null
   */
  getUrlTicketParam() {
    try {
      const url = new URL(window.location.href);
      return url.searchParams.get('ticket');
    } catch {
      return null;
    }
  }

  /**
   * Handles ticket selection from the dropdown
   * Validates ticket status and city, then updates constraints and UI
   * Shows confirmation dialog if switching tickets with pending selections
   * @param {Object} ticketData - Ticket selection event data
   * @param {string} ticketData.ticketId - Selected ticket ID
   * @param {number} [ticketData.classesLimit] - Classes limit override
   * @param {number} [ticketData.daysLimit] - Days limit override
   * @param {string} [ticketData.city] - City override
   * @param {boolean} [ticketData.skipConfirmation] - Skip confirmation dialog
   * @private
   */
  async handleTicketSelect(ticketData) {
    const { ticketId, classesLimit, daysLimit, city, skipConfirmation = false } = ticketData;
    this.log('handleTicketSelect:', { ticketId, classesLimit, daysLimit, city });

    // Check for pending selections before switching tickets
    if (!skipConfirmation && this.state.selectedTicketId && this.state.selectedTicketId !== ticketId) {
      if (this.hasPendingSelections()) {
        const { confirmed } = await NotificationModal.confirm(
          'You have unsaved class selections. Discard changes and switch tickets?',
          {
            confirmText: 'Discard & Switch',
            cancelText: 'Stay',
            type: 'warning',
            showLoading: false
          }
        );

        if (!confirmed) {
          // User chose to stay - reset the dropdown to current ticket
          this.resetTicketSelector(this.state.selectedTicketId);
          return;
        }
        // User confirmed discard - proceed with switch
      }
    }

    // Find ticket data
    const ticketRecord = this.findTicketById(ticketId);

    if (!ticketRecord) {
      this.log('Ticket not found:', ticketId);
      NotificationModal.error('This ticket is no longer eligible for registration.');
      this.resetTicketSelector();
      return;
    }

    // CRITICAL: Validate ticket status before allowing selection
    // Per TICKET_SYSTEM_SPECIFICATION.md lines 239-243: only "active" tickets can be used
    const normalizedStatus = (ticketRecord.status || '').toLowerCase();

    if (normalizedStatus !== 'active') {
      let statusMessage = 'This ticket cannot be used for registration.';
      if (normalizedStatus === 'cancelled') {
        statusMessage = 'This ticket has been cancelled and cannot be used for registration';
      } else if (normalizedStatus === 'refunded') {
        statusMessage = 'This ticket has been refunded and cannot be used for registration';
      }

      NotificationModal.error(statusMessage);
      this.resetTicketSelector();
      return;
    }

    const normalizedTicketCity = this.normalizeCity(ticketRecord.city || city);
    if (this.eventCity && (!normalizedTicketCity || normalizedTicketCity !== this.eventCity)) {
      NotificationModal.error('This ticket belongs to a different event city and cannot be used here.');
      this.resetTicketSelector();
      return;
    }

    // Clear previous selections if switching tickets
    if (this.state.selectedTicketId && this.state.selectedTicketId !== ticketId) {
      this.log('Switching tickets, clearing selections');
      this.clearAllSelections();
    }

    const resolvedClassesLimit = this.getConstraintValue(
      ticketRecord,
      'classes_limit',
      typeof classesLimit === 'number' ? classesLimit : -1
    );
    const resolvedDaysLimit = this.getConstraintValue(
      ticketRecord,
      'days_limit',
      typeof daysLimit === 'number' ? daysLimit : -1
    );

    this.state.selectedTicketId = ticketId;
    this.state.ticketConstraints = {
      classesLimit: resolvedClassesLimit,
      daysLimit: resolvedDaysLimit,
      city: normalizedTicketCity,
    };

    // Update URL with selected ticket ID for shareable links
    this.updateUrlTicketParam(ticketId);

    // Note: Registered classes from metafield are shown in overview table only
    // Sidebar is for NEW selections, so we don't pre-populate from selected_classes

    this.setTicketSelectedState(true);
    this.updateAvailability();
    this.updateSummary();
  }

  /**
   * Handles class checkbox toggle events
   * Validates selection and updates state accordingly
   * @param {HTMLInputElement} checkbox - The checkbox that was toggled
   * @private
   */
  handleClassToggle(checkbox) {
    this.log('handleClassToggle called');
    const cardBlock = checkbox.closest('[data-registration-mode]');
    if (!cardBlock) {
      this.log('No parent cardBlock found');
      return;
    }


    if (!this.state.selectedTicketId) {
      this.log('No ticket selected');
      checkbox.checked = false;
      NotificationModal.error('Please select a ticket first');
      return;
    }

    const classId = cardBlock.dataset.classId;
    const date = cardBlock.dataset.classDate;
    const time = cardBlock.dataset.classTime;
    const kitVariantId = cardBlock.dataset.kitVariantId;
    const kitProductTitle = cardBlock.dataset.kitProductTitle || null;
    const kitProductPrice = cardBlock.dataset.kitProductPrice || null;
    const kitProductPriceFormatted = cardBlock.dataset.kitProductPriceFormatted || null;

    // Get capacity data for validation
    const spotsRemaining = cardBlock.dataset.spotsRemaining;
    const registrantsLimit = cardBlock.dataset.registrantsLimit;

    // Get class name from DOM
    let className = 'Class';
    const titleElement = cardBlock.querySelector('.class-card__title');
    if (titleElement) {
      className = titleElement.textContent.trim();
    }

    this.log('Class toggle:', classId, checkbox.checked ? 'selected' : 'deselected');

    if (checkbox.checked) {
      // Validate selection (including capacity check)
      const canSelect = this.validateSelection(classId, date, time, spotsRemaining, registrantsLimit, className);
      if (!canSelect) {
        checkbox.checked = false;
        return;
      }

      this.addClassSelection(classId, className, date, time, kitVariantId, kitProductTitle, kitProductPrice, kitProductPriceFormatted);
    } else {
      this.removeClassSelection(classId);
    }
    this.updateAvailability();
    this.updateSummary();

    // Dispatch event for class cards to update their button state
    document.dispatchEvent(new CustomEvent('selection:changed', {
      detail: { classId, selected: checkbox.checked }
    }));
  }


  /**
   * Validates whether a class can be selected based on ticket constraints
   * Checks class limits, time slot conflicts, day restrictions, and class capacity
   * @param {string} classId - Class ID to validate
   * @param {string} date - Class date
   * @param {string} timeSlot - Class time slot
   * @param {string|number} [spotsRemaining] - Number of spots remaining in the class
   * @param {string|number} [registrantsLimit] - Maximum registrants for the class
   * @param {string} [className] - Class name for error messages
   * @returns {boolean} True if selection is valid
   * @private
   */
  validateSelection(classId, date, timeSlot, spotsRemaining, registrantsLimit, className) {
    if (!classId || !date || !timeSlot) {
      NotificationModal.error('This class is missing required registration details. Please refresh and try again.');
      return false;
    }

    const ticketId = this.state.selectedTicketId;
    const selections = this.state.selections[ticketId] || [];

    // Get already registered classes from ticket record (committed to server)
    const ticketRecord = this.findTicketById(ticketId);
    const registeredClasses = ticketRecord?.selected_classes || [];

    // Check if already registered for this exact class (check BEFORE capacity)
    const alreadyRegistered = registeredClasses.find(
      (c) => c.class_id === classId && c.date === date && c.time_slot === timeSlot
    );
    if (alreadyRegistered) {
      NotificationModal.warning('You are already registered for this class');
      return false;
    }

    // Check if already in pending selections (prevent duplicates)
    const alreadyPending = selections.find(
      (s) => s.class_id === classId && s.date === date && s.time_slot === timeSlot
    );
    if (alreadyPending) {
      NotificationModal.warning('This class is already in your selections');
      return false;
    }

    // Check class capacity
    // spotsRemaining comes as string from dataset, convert to number
    const spots = spotsRemaining !== undefined && spotsRemaining !== '' ? Number(spotsRemaining) : null;
    const limit = registrantsLimit !== undefined && registrantsLimit !== '' ? Number(registrantsLimit) : null;

    // Only check capacity if the class has a registrants limit set
    if (limit !== null && spots !== null && spots <= 0) {
      const displayName = className || 'This class';
      NotificationModal.warning(`${displayName} is full. No spots remaining.`);
      return false;
    }

    // Combine pending selections with already registered classes for constraint validation
    const allClasses = [...registeredClasses, ...selections];

    const classesLimit = typeof this.state.ticketConstraints.classesLimit === 'number'
      ? this.state.ticketConstraints.classesLimit
      : -1;
    const daysLimit = typeof this.state.ticketConstraints.daysLimit === 'number'
      ? this.state.ticketConstraints.daysLimit
      : -1;

    // Check class limit (including already registered classes)
    if (classesLimit === 0) {
      NotificationModal.warning('This ticket does not allow class registration');
      return false;
    }

    if (classesLimit !== -1 && allClasses.length >= classesLimit) {
      NotificationModal.warning(`You can only register for ${classesLimit} class(es) with this ticket`);
      return false;
    }

    // Check time conflicts (same slot, or overlapping a class's optional end time)
    // Check against both pending selections AND already registered classes
    if (this.config.enforceTimeConflicts) {
      const candidate = { date, time: timeSlot, endTime: getClassEndTime(classId) };

      const conflictInSelections = selections.find((s) => this.classesConflict(candidate, s));
      if (conflictInSelections) {
        NotificationModal.warning(`This class overlaps with ${conflictInSelections.class_name || 'a class you already selected'}`);
        return false;
      }

      const conflictInRegistered = registeredClasses.find((c) => this.classesConflict(candidate, c));
      if (conflictInRegistered) {
        NotificationModal.warning(`This class overlaps with ${conflictInRegistered.class_name || 'a class you are already registered for'}`);
        return false;
      }
    }

    // Check day restrictions (including already registered classes)
    if (this.config.enforceDayRestrictions) {
      if (daysLimit === 0) {
        NotificationModal.warning('This ticket does not allow class day selections');
        return false;
      }

      if (daysLimit !== -1) {
        const uniqueDates = new Set(allClasses.map((c) => c.date));
        uniqueDates.add(date);

        if (uniqueDates.size > daysLimit) {
          const suffix = daysLimit === 1 ? '' : 's';
          NotificationModal.warning(`Your ticket only allows classes on ${daysLimit} day${suffix}`);
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Whether a candidate class overlaps a selected or registered class.
   * End times are read from the class cards so server records pick up later edits.
   * @param {{date: string, time: string, endTime: string}} candidate
   * @param {ClassSelection} existing
   * @returns {boolean}
   * @private
   */
  classesConflict(candidate, existing) {
    return classesOverlap(candidate, this.toScheduledClass(existing));
  }

  /**
   * Maps a selected or registered class to the shape used for overlap checks
   * @param {ClassSelection} selection
   * @returns {{date: string, time: string, endTime: string}}
   * @private
   */
  toScheduledClass(selection) {
    return { date: selection.date, time: selection.time_slot || '', endTime: getClassEndTime(selection.class_id) };
  }

  /**
   * Adds a class selection to state and auto-adds associated kit
   * @param {string} classId - Class ID
   * @param {string} className - Class display name
   * @param {string} date - Class date
   * @param {string} timeSlot - Class time slot
   * @param {string|null} kitVariantId - Associated kit variant ID
   * @param {string|null} kitProductTitle - Kit product title
   * @param {string|null} kitProductPrice - Kit price in cents
   * @param {string|null} kitProductPriceFormatted - Formatted kit price
   * @private
   */
  addClassSelection(classId, className, date, timeSlot, kitVariantId, kitProductTitle, kitProductPrice, kitProductPriceFormatted) {
    const ticketId = this.state.selectedTicketId;
    if (!this.state.selections[ticketId]) {
      this.state.selections[ticketId] = [];
    }

    // Get class name from DOM if not provided (avoiding lookups during render)
    const resolvedClassName = className || (() => {
      const classCard = document.querySelector(`[data-class-id="${classId}"]`);
      return classCard
        ? classCard.querySelector('.class-card__title')?.textContent?.trim() || 'Class'
        : 'Class';
    })();

    const selection = {
      class_id: classId,
      class_name: resolvedClassName,
      date: date,
      time_slot: timeSlot,
      kit_variant_id: kitVariantId || null,
      kit_title: kitProductTitle || null,
      kit_price: kitProductPrice || null,
      kit_price_formatted: kitProductPriceFormatted || null,
      registered_at: new Date().toISOString(),
    };

    this.log('addClassSelection:', selection);
    this.state.selections[ticketId].push(selection);
    this.log('Total selections:', this.state.selections[ticketId].length);

    // Auto-add kit if class has one (default checked for purchase)
    if (kitVariantId) {
      this.log('Auto-adding kit for class:', classId, 'kit:', kitVariantId);
      this.addKit(kitVariantId, classId, resolvedClassName, true, kitProductTitle, kitProductPrice, kitProductPriceFormatted);
    }

    // Save to localStorage to persist selections
    this.saveToLocalStorage();
  }

  /**
   * Removes a class selection and its associated kit
   * @param {string} classId - Class ID to remove
   * @private
   */
  removeClassSelection(classId) {
    const ticketId = this.state.selectedTicketId;
    if (!this.state.selections[ticketId]) return;

    this.log('removeClassSelection:', classId);

    const selectionIndex = this.state.selections[ticketId].findIndex(
      (s) => s.class_id === classId
    );

    if (selectionIndex === -1) {
      this.log('Class not found in selections');
      return;
    }

    const selection = this.state.selections[ticketId][selectionIndex];

    // Auto-remove linked kit (kits are always auto-managed now)
    if (selection.kit_variant_id) {
      this.log('Auto-removing linked kit:', selection.kit_variant_id);
      this.removeKitFromClass(selection.kit_variant_id, classId);
    }

    this.state.selections[ticketId].splice(selectionIndex, 1);
    this.log('Removed selection, total remaining:', this.state.selections[ticketId].length);

    // Uncheck the checkbox
    const safeId = this.sanitizeId(classId);
    const checkbox = document.querySelector(`#class-${safeId}`);
    if (checkbox) {
      this.log('Unchecking checkbox for:', classId);
      checkbox.checked = false;
    } else {
      this.log('WARNING: Checkbox not found for:', classId, 'tried:', `#class-${safeId}`);
    }

    // Save to localStorage to persist changes
    this.saveToLocalStorage();

    this.updateAvailability();
    this.updateSummary();
  }

  /**
   * Adds or updates a kit in state, linking it to a class
   * @param {string} kitVariantId - Kit variant ID
   * @param {string|null} [linkedClassId] - Class ID to link
   * @param {string|null} [linkedClassName] - Class name to link
   * @param {boolean} [checked=true] - Whether kit is selected for purchase
   * @param {string|null} [title] - Kit product title
   * @param {string|null} [price] - Kit price in cents
   * @param {string|null} [priceFormatted] - Formatted kit price
   * @private
   */
  addKit(kitVariantId, linkedClassId = null, linkedClassName = null, checked = true, title = null, price = null, priceFormatted = null) {
    this.log('addKit called:', { kitVariantId, linkedClassId, linkedClassName, title, price, priceFormatted });

    if (!this.isKitAvailable(kitVariantId)) {
      this.log('Kit is not on sale online, not offering it:', kitVariantId);
      return;
    }

    // Cache kit info if we have valid data (not fallback 'Kit')
    if (title && title !== 'Kit') {
      this.state.kitInfoCache[kitVariantId] = { title, price, priceFormatted };
      this.log('Cached kit info:', kitVariantId, this.state.kitInfoCache[kitVariantId]);
    }

    // Use cached info if available and passed title is fallback
    const cachedInfo = this.state.kitInfoCache[kitVariantId];
    const resolvedTitle = (title && title !== 'Kit') ? title : (cachedInfo?.title || title);
    const resolvedPrice = price || cachedInfo?.price || null;
    const resolvedPriceFormatted = priceFormatted || cachedInfo?.priceFormatted || null;

    this.log('Resolved kit info:', { resolvedTitle, resolvedPrice, resolvedPriceFormatted });

    if (!this.state.kits[kitVariantId]) {
      this.state.kits[kitVariantId] = {
        quantity: 0,
        linkedClasses: [],
        linkedClassNames: [],
        checked: checked,
        title: resolvedTitle,
        price: resolvedPrice,
        priceFormatted: resolvedPriceFormatted,
      };
      this.log('Created new kit entry:', this.state.kits[kitVariantId]);
    } else {
      // Update title if we now have better info
      if (resolvedTitle && resolvedTitle !== 'Kit' && (!this.state.kits[kitVariantId].title || this.state.kits[kitVariantId].title === 'Kit')) {
        this.state.kits[kitVariantId].title = resolvedTitle;
        this.state.kits[kitVariantId].price = resolvedPrice;
        this.state.kits[kitVariantId].priceFormatted = resolvedPriceFormatted;
        this.log('Updated existing kit with better info:', this.state.kits[kitVariantId]);
      }
      this.log('Kit already exists:', this.state.kits[kitVariantId]);
    }

    if (linkedClassId && !this.state.kits[kitVariantId].linkedClasses.includes(linkedClassId)) {
      this.state.kits[kitVariantId].linkedClasses.push(linkedClassId);
      if (linkedClassName) {
        this.state.kits[kitVariantId].linkedClassNames.push(linkedClassName);
      }
    }

    this.state.kits[kitVariantId].quantity = this.state.kits[kitVariantId].linkedClasses.length;
  }

  /**
   * Whether a kit can still be bought online. Kit products are set out of stock in Shopify
   * when online kit sales close before the event, so an unavailable kit is never offered.
   * Reads the class cards' `data-kit-available`; kits without a card on the page stay on sale.
   * @param {string} kitVariantId - Kit variant ID
   * @returns {boolean}
   */
  isKitAvailable(kitVariantId) {
    if (!kitVariantId) return false;
    const selector = `class-card-component[data-kit-variant-id="${CSS.escape(String(kitVariantId))}"]`;
    return document.querySelector(selector)?.dataset.kitAvailable !== 'false';
  }

  /**
   * Gets cached kit info by variant ID
   * @param {string} kitVariantId - Kit variant ID
   * @returns {Object|null} Cached kit info or null if not found
   */
  getKitInfo(kitVariantId) {
    return this.state.kitInfoCache[kitVariantId] || null;
  }

  /**
   * Toggles the checked state of a kit for purchase
   * @param {string} kitVariantId - Kit variant ID to toggle
   * @private
   */
  toggleKitChecked(kitVariantId) {
    if (this.state.kits[kitVariantId]) {
      this.state.kits[kitVariantId].checked = !this.state.kits[kitVariantId].checked;
      this.log('Kit checked toggled:', kitVariantId, 'checked:', this.state.kits[kitVariantId].checked);

      // Directly update the checkbox in the summary panel to avoid full re-render
      if (this.dom.kitsList) {
        const checkbox = this.dom.kitsList.querySelector(`[data-kit-variant-id="${kitVariantId}"]`);
        if (checkbox && checkbox instanceof HTMLInputElement) {
          checkbox.checked = this.state.kits[kitVariantId].checked;
        }
      }

      // Save to localStorage to persist kit state changes
      this.saveToLocalStorage();

      // Update CTA visibility when kit checked state changes
      this.updateCTAs();
    }
  }

  /**
   * Removes a kit entirely from state
   * @param {string} kitVariantId - Kit variant ID to remove
   * @private
   */
  removeKit(kitVariantId) {
    delete this.state.kits[kitVariantId];

    // Uncheck all kit checkboxes for this kit
    const kitCheckboxes = document.querySelectorAll(`[data-kit-checkbox][id^="kit-"]`);
    kitCheckboxes.forEach((checkbox) => {
      const cardBlock = checkbox.closest('[data-registration-mode]');
      if (cardBlock && cardBlock.dataset.kitVariantId === kitVariantId) {
        checkbox.checked = false;
      }
    });

    this.updateSummary();

    // Dispatch event so overview can update its kit column state
    document.dispatchEvent(new CustomEvent('kit:changed', { detail: { kitVariantId, action: 'removed' } }));
  }

  /**
   * Updates the attendee name for a ticket and saves to server immediately
   * @param {string} ticketId - Ticket ID
   * @param {string} name - Attendee name
   * @param {boolean} [saveToServer=false] - Whether to save to server immediately
   * @returns {Promise<boolean>} True if save was successful
   */
  async updateAttendeeName(ticketId, name, saveToServer = false) {
    if (!ticketId) return false;

    // Trim and enforce max length (100 chars) as defensive measure
    const sanitizedName = name.trim().substring(0, 100);
    this.state.attendeeNames[ticketId] = sanitizedName;
    this.log('Updated attendee name for ticket:', ticketId, '→', sanitizedName);

    // Save to localStorage
    this.saveToLocalStorage();

    // Save to server immediately if requested
    if (saveToServer) {
      this.dirtyAttendeeTicketIds.add(ticketId);
      const success = await this.saveAttendeeNameToServer(ticketId, sanitizedName);
      if (!success) {
        return false;
      }
    }

    // Dispatch event to update UI
    document.dispatchEvent(new CustomEvent('attendee:updated', {
      detail: {
        ticketId: ticketId,
        attendeeName: sanitizedName,
      },
    }));

    return true;
  }

  /**
   * Saves attendee name to server immediately
   * Backend endpoint: POST /apps/{registration|registration-dev}/update-attendee
   * Request body: { ticket_id: string, attendee_name: string }
   * @param {string} ticketId - Ticket ID
   * @param {string} name - Attendee name
   * @returns {Promise<boolean>} True if save was successful
   * @private
   */
  async saveAttendeeNameToServer(ticketId, name) {
    const subpath = window.REGISTRATION_DEV ? 'registration-dev' : 'registration';
    const apiEndpoint = `/apps/${subpath}/update-attendee`;

    try {
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ticket_id: ticketId,
          attendee_name: name,
        }),
      });

      if (!response.ok) {
        const responseBody = await response.json().catch(() => ({}));
        throw new Error(
          responseBody.error?.message || responseBody.message || 'Failed to save attendee name'
        );
      }

      const result = await response.json();
      this.log('Attendee name saved to server:', result);

      // Update the ticket record in memory
      const ticketRecord = this.findTicketById(ticketId);
      if (ticketRecord) {
        ticketRecord.attendee_name = name;
      }

      // Every committed action ends with a full authoritative table refresh.
      this.dirtyAttendeeTicketIds.delete(ticketId);
      await this.refreshCommittedSchedule();
      this.notifyScheduleChanged(ticketId);

      return true;
    } catch (error) {
      // Check if it's a network error - use demo mode
      if (window.REGISTRATION_DEMO === true && (error.name === 'TypeError' || error.message.includes('fetch'))) {
        this.log('API unavailable, using demo mode for attendee name save');

        this.dirtyAttendeeTicketIds.delete(ticketId);

        // Update ticket data in memory
        const ticketRecord = this.findTicketById(ticketId);
        if (ticketRecord) {
          ticketRecord.attendee_name = name;
        }

        return true;
      }

      const ticketRecord = this.findTicketById(ticketId);
      this.state.attendeeNames[ticketId] = ticketRecord?.attendee_name || '';
      this.dirtyAttendeeTicketIds.delete(ticketId);
      this.saveToLocalStorage();
      await this.refreshCommittedSchedule();
      console.error('Failed to save attendee name:', error);
      NotificationModal.error(error.message || 'Failed to save attendee name. Please try again.');
      return false;
    }
  }

  /**
   * Gets the attendee name for a ticket
   * @param {string} ticketId - Ticket ID
   * @returns {string} Attendee name or empty string
   */
  getAttendeeName(ticketId) {
    return this.state.attendeeNames[ticketId] || '';
  }

  /**
   * Removes a kit's association with a specific class
   * If kit has no more linked classes, removes it entirely
   * @param {string} kitVariantId - Kit variant ID
   * @param {string} classId - Class ID to unlink
   * @private
   */
  removeKitFromClass(kitVariantId, classId) {
    if (!this.state.kits[kitVariantId]) return;

    const index = this.state.kits[kitVariantId].linkedClasses.indexOf(classId);
    if (index > -1) {
      this.state.kits[kitVariantId].linkedClasses.splice(index, 1);
    }

    let kitRemoved = false;
    if (this.state.kits[kitVariantId].linkedClasses.length === 0) {
      delete this.state.kits[kitVariantId];
      kitRemoved = true;
    } else {
      this.state.kits[kitVariantId].quantity = this.state.kits[kitVariantId].linkedClasses.length;
    }

    // Uncheck the specific kit checkbox
    const safeId = this.sanitizeId(classId);
    const kitCheckbox = document.querySelector(`#kit-${safeId}`);
    if (kitCheckbox) {
      kitCheckbox.checked = false;
    }

    // Dispatch event so overview can update its kit column state
    if (kitRemoved) {
      document.dispatchEvent(new CustomEvent('kit:changed', { detail: { kitVariantId, action: 'removed' } }));
    }
  }

  /**
   * Clears all class and kit selections for the current ticket
   * @private
   */
  clearAllSelections() {
    if (this.state.selectedTicketId) {
      this.state.selections[this.state.selectedTicketId] = [];
    }
    this.state.kits = {};

    // Uncheck all checkboxes
    document.querySelectorAll('[data-class-checkbox]:checked').forEach((checkbox) => {
      checkbox.checked = false;
    });
    document.querySelectorAll('[data-kit-checkbox]:checked').forEach((checkbox) => {
      checkbox.checked = false;
    });

    // Clear localStorage when clearing all selections
    try {
      localStorage.removeItem('class_registration_temp');
      this.log('Cleared temp state from localStorage');

      // Note: registration:updated event is for potential future use
      // The overview table should only update on page reload, not in real-time
      document.dispatchEvent(new CustomEvent('registration:updated', {
        detail: null
      }));
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
    }

    this.updateAvailability();
    this.updateSummary();
  }

  /**
   * Clears temporary selection state (sidebar) without removing committed registrations
   * Preserves registered classes in overview table
   * @param {boolean} [keepTicketContext=false] - If true, keeps ticket selected so user can continue registering
   * @private
   */
  clearTemporaryState(keepTicketContext = false) {
    // Clear pending selections and kits, but optionally keep ticket context
    this.state.selections = {};
    this.state.kits = {};
    this.state.timeSlotMap = {};

    if (keepTicketContext) {
      // Keep ticket selected so user can see overview and register more classes
      this.log('Cleared pending selections but keeping ticket context:', this.state.selectedTicketId);
    } else {
      // Full reset - clears everything including ticket selection
      this.state.selectedTicketId = null;
      this.state.ticketConstraints = {};
      this.state.attendeeNames = {};
      this.dirtyAttendeeTicketIds.clear();
      this.setTicketSelectedState(false);
      // Clear URL param when fully deselecting ticket
      this.updateUrlTicketParam(null);
    }

    // Clear localStorage temporary data (committed data now comes from server/this.tickets)
    try {
      localStorage.removeItem('class_registration_temp');
      this.log('Cleared temporary selection state from localStorage');
    } catch (error) {
      console.error('Failed to clear temporary state:', error);
    }

    // Uncheck all class checkboxes in the grid
    const checkboxes = document.querySelectorAll('[data-class-checkbox]');
    checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });

    // Update sidebar summary to reflect cleared state
    this.updateSummary();
  }

  /**
   * Updates class card availability based on current selections and constraints
   * Disables cards that would violate class limits, time conflicts, or day restrictions
   * @private
   */
  updateAvailability() {
    if (!this.state.selectedTicketId) return;

    const ticketId = this.state.selectedTicketId;
    const selections = this.state.selections[ticketId] || [];

    // Get already registered classes from ticket record (committed to server)
    const ticketRecord = this.findTicketById(ticketId);
    const registeredClasses = ticketRecord?.selected_classes || [];

    // Combine pending selections with registered classes for availability checks
    const allClasses = [...registeredClasses, ...selections];

    const classesLimit = typeof this.state.ticketConstraints.classesLimit === 'number'
      ? this.state.ticketConstraints.classesLimit
      : -1;
    const daysLimit = typeof this.state.ticketConstraints.daysLimit === 'number'
      ? this.state.ticketConstraints.daysLimit
      : -1;

    // Resolve end times once so the per-card loop doesn't rescan the DOM
    const scheduledClasses = this.config.enforceTimeConflicts ? allClasses.map((c) => this.toScheduledClass(c)) : [];

    // Pre-build lookup sets for O(1) checks instead of O(n) some() calls
    const selectedDates = new Set();
    const registeredClassKeys = new Set();

    allClasses.forEach((c) => {
      selectedDates.add(c.date);
    });

    // Track registered classes to mark them as already registered
    registeredClasses.forEach((c) => {
      registeredClassKeys.add(`${c.class_id}-${c.date}-${c.time_slot}`);
    });

    // Get all class cards
    const classCards = document.querySelectorAll('[data-registration-mode="true"]');

    classCards.forEach((card) => {
      const classId = card.dataset.classId;
      const date = card.dataset.classDate;
      const timeSlot = card.dataset.classTime;
      const checkbox = card.querySelector('[data-class-checkbox]');
      const registerButton = card.querySelector('[data-class-register-button]');

      if (!checkbox) return;

      // Skip if already selected (pending) or already registered (committed)
      const classKey = `${classId}-${date}-${timeSlot}`;
      if (checkbox.checked || registeredClassKeys.has(classKey)) return;

      let shouldDisable = false;

      // Past classes cannot be registered for
      if (card.dataset.classPast === 'true') {
        shouldDisable = true;
      }

      // Check class limit (including registered classes)
      if (!shouldDisable && classesLimit === 0) {
        shouldDisable = true;
      } else if (!shouldDisable && classesLimit !== -1 && allClasses.length >= classesLimit) {
        shouldDisable = true;
      }

      // Check time conflicts (same slot, or overlapping a class's optional end time)
      if (!shouldDisable && this.config.enforceTimeConflicts) {
        const candidate = { date, time: timeSlot, endTime: card.dataset.classEndTime || '' };
        if (scheduledClasses.some((c) => classesOverlap(candidate, c))) {
          shouldDisable = true;
        }
      }

      // Check day restrictions
      if (!shouldDisable && this.config.enforceDayRestrictions) {
        if (daysLimit === 0) {
          shouldDisable = true;
        } else if (daysLimit !== -1) {
          const dateAlreadySelected = selectedDates.has(date);
          if (!dateAlreadySelected && selectedDates.size >= daysLimit) {
            shouldDisable = true;
          }
        }
      }

      // Apply disabled state
      if (shouldDisable) {
        card.classList.add('class-card-block--disabled');
        checkbox.disabled = true;
        if (registerButton) registerButton.disabled = true;
      } else {
        card.classList.remove('class-card-block--disabled');
        checkbox.disabled = false;
        if (registerButton) registerButton.disabled = false;
      }
    });
  }

  /**
   * Updates UI elements to reflect current state (checkboxes, kit states)
   * Handles backward compatibility migration for old localStorage data
   * @private
   */
  updateUIFromState() {
    const ticketId = this.state.selectedTicketId;
    if (!ticketId) return;

    const selections = this.state.selections[ticketId] || [];
    this.log('updateUIFromState: Updating UI for', selections.length, 'selections');

    // Track valid selections (classes that still exist in DOM)
    const validSelections = [];
    let needsMigration = false;

    selections.forEach((selection) => {
      const safeId = this.sanitizeId(selection.class_id);
      const checkbox = document.querySelector(`#class-${safeId}`);

      this.log('Looking for checkbox:', `#class-${safeId}`, 'Found:', !!checkbox);

      if (checkbox) {
        checkbox.checked = true;

        // Backward compatibility: Migrate old data that doesn't have class_name
        if (!selection.class_name) {
          const classCard = document.querySelector(`[data-class-id="${selection.class_id}"]`);
          if (classCard) {
            selection.class_name = classCard.querySelector('.class-card__title')?.textContent?.trim() || 'Class';
            needsMigration = true;
            this.log('Migrated class_name for:', selection.class_id, '→', selection.class_name);
          }
        }

        validSelections.push(selection);

        if (selection.kit_variant_id) {
          const kitCheckbox = document.querySelector(`#kit-${safeId}`);
          if (kitCheckbox) {
            kitCheckbox.checked = true;
            // Kit data is already restored from localStorage, no need to re-add
          }
        }
      } else {
        this.log('WARNING: Checkbox not found for class (removed from page):', selection.class_id);
      }
    });

    // Update state to only include valid selections and save if migration occurred
    // NOTE: Only save to localStorage if we actually migrated data or found stale selections
    // Don't save if checkboxes are just missing from DOM (timing/collapsed sections)
    if (validSelections.length !== selections.length || needsMigration) {
      if (validSelections.length !== selections.length) {
        // Only clean up if we actually found some valid selections (prevents clearing on initial load)
        if (validSelections.length > 0) {
          this.log('Cleaned up', selections.length - validSelections.length, 'stale selections');
          this.state.selections[ticketId] = validSelections;
          this.saveToLocalStorage();
        } else {
          this.log('WARNING: Could not find any checkboxes in DOM for', selections.length, 'selections');
          this.log('This may be due to collapsed sections or timing. Not updating localStorage.');
        }
      }
      if (needsMigration) {
        this.log('Migrated localStorage data to include class names');
        this.saveToLocalStorage();
      }
    }
  }

  /**
   * Updates the summary panel with current selections and kits
   * @private
   */
  updateSummary() {
    if (!this.dom.summaryPanel) return;

    const ticketId = this.state.selectedTicketId;
    const selections = ticketId ? this.state.selections[ticketId] || [] : [];
    const hasSelections = selections.length > 0 || Object.keys(this.state.kits).length > 0;

    this.log('updateSummary:', {
      ticketId,
      selectionsCount: selections.length,
      kitsCount: Object.keys(this.state.kits).length,
      hasSelections,
    });

    // Show/hide summary panel
    this.dom.summaryPanel.hidden = !hasSelections;

    if (this.dom.emptyState) this.dom.emptyState.hidden = hasSelections;
    if (this.dom.ticketsContainer) this.dom.ticketsContainer.hidden = selections.length === 0;
    if (this.dom.footer) this.dom.footer.hidden = !hasSelections;
    if (this.dom.clearAllBtn) this.dom.clearAllBtn.hidden = !hasSelections;

    // Render selected classes
    if (this.dom.ticketsContainer && selections.length > 0) {
      this.renderClassSelections(this.dom.ticketsContainer, selections);
    }

    // Render kits
    if (this.dom.kitsContainer) {
      const hasKits = Object.keys(this.state.kits).length > 0;
      this.dom.kitsContainer.hidden = !hasKits;
      if (hasKits) {
        this.renderKits(this.dom.kitsContainer);
      }
    }

    // Update attendee name display in summary
    const summaryAttendee = document.querySelector('[data-summary-attendee]');
    if (summaryAttendee && ticketId) {
      const attendeeName = this.getAttendeeName(ticketId);
      if (attendeeName) {
        summaryAttendee.textContent = `For: ${attendeeName}`;
        summaryAttendee.hidden = false;
      } else {
        summaryAttendee.hidden = true;
      }
    }

    // Update CTA visibility based on state
    this.updateCTAs();

    // Update collapsed bar count
    this.updateCollapsedState();
  }

  /**
   * Checks if any kits exist in state
   * @returns {boolean} True if at least one kit exists
   */
  get hasKits() {
    return Object.keys(this.state.kits).length > 0;
  }

  /**
   * Checks if any kits are checked for purchase
   * @returns {boolean} True if at least one kit is checked
   */
  get anyKitsChecked() {
    return Object.values(this.state.kits).some(kit => kit.checked);
  }

  /**
   * Checks if all kits are unchecked
   * @returns {boolean} True if kits exist but none are checked
   */
  get allKitsUnchecked() {
    return this.hasKits && !this.anyKitsChecked;
  }

  /**
   * Checks if there are any class selections for the current ticket
   * @returns {boolean} True if at least one class is selected
   */
  get hasClassSelections() {
    const ticketId = this.state.selectedTicketId;
    const selections = ticketId ? this.state.selections[ticketId] || [] : [];
    return selections.length > 0;
  }

  /**
   * Updates CTA button visibility based on current state
   *
   * Three purchase flows supported:
   * 1. Classes + Kits (together): Classes selected, kits checked
   *    → Show both buttons: "Register and Purchase Kits" (primary) + "Register for Classes Only" (secondary)
   *
   * 2. Classes Only (kit later): Classes selected, kits unchecked or no kits
   *    → Show "Register for Classes Only" (primary)
   *
   * 3. Kits Only (after registration): No classes selected, only kits available
   *    → Show "Purchase Kits" (primary) - kits can be purchased after registration
   * @private
   */
  updateCTAs() {
    const classesOnlyBtn = document.querySelector('[data-submit-registration-classes-only]');
    const withKitsBtn = document.querySelector('[data-submit-registration-with-kits]');
    const disabledReasonEl = document.querySelector('[data-disabled-reason]');

    if (!classesOnlyBtn || !withKitsBtn) {
      return;
    }

    // Determine which buttons to show based on state
    // Flow 1: Classes + Kits together (kits are checked)
    const showWithKits = this.anyKitsChecked;

    // Flow 2 & 3: Classes only OR Kits only after registration
    const showClassesOnly = this.hasClassSelections || (!this.hasClassSelections && this.hasKits && !this.anyKitsChecked);

    // Update visibility
    withKitsBtn.hidden = !showWithKits;
    classesOnlyBtn.hidden = !showClassesOnly;

    // Determine if there's a reason why user can't add more classes
    let disabledReason = null;

    if (!showWithKits && !showClassesOnly && this.state.selectedTicketId) {
      const ticketId = this.state.selectedTicketId;
      const ticketRecord = this.findTicketById(ticketId);
      const registeredClasses = ticketRecord?.selected_classes || [];

      const classesLimit = typeof this.state.ticketConstraints.classesLimit === 'number'
        ? this.state.ticketConstraints.classesLimit
        : -1;
      const daysLimit = typeof this.state.ticketConstraints.daysLimit === 'number'
        ? this.state.ticketConstraints.daysLimit
        : -1;

      // Check if at class limit
      if (classesLimit !== -1 && registeredClasses.length >= classesLimit) {
        disabledReason = `You have reached your limit of ${classesLimit} class${classesLimit === 1 ? '' : 'es'} for this ticket.`;
      }
      // Check if at days limit
      else if (daysLimit !== -1) {
        const registeredDates = new Set(registeredClasses.map(c => c.date));
        if (registeredDates.size >= daysLimit) {
          disabledReason = `You have registered for classes on ${daysLimit} day${daysLimit === 1 ? '' : 's'}, which is the limit for this ticket.`;
        }
      }
      // No selections and no limits hit - guide the user
      else if (!this.hasClassSelections && !this.hasKits) {
        disabledReason = 'Select classes above to register for this event.';
      }
    }
    // Ticket selected but no selections made
    else if (!showWithKits && !showClassesOnly && !this.state.selectedTicketId) {
      // No ticket selected yet - this is handled by the empty state
    }
    // Ticket selected, no class selections, no kits - show guidance
    else if (!this.hasClassSelections && !this.hasKits && this.state.selectedTicketId) {
      disabledReason = 'Select classes above to register for this event.';
    }

    // Update disabled reason element
    if (disabledReasonEl) {
      if (disabledReason) {
        disabledReasonEl.textContent = disabledReason;
        disabledReasonEl.hidden = false;
      } else {
        disabledReasonEl.hidden = true;
      }
    }

    // Update button text based on context
    if (!this.hasClassSelections && this.hasKits) {
      // Flow 3: Kits only (after registration)
      classesOnlyBtn.textContent = 'Purchase Kits';
      withKitsBtn.textContent = 'Purchase Kits';
    } else {
      // Flow 1 & 2: Classes with or without kits
      classesOnlyBtn.textContent = 'Register for classes only';
      withKitsBtn.textContent = 'Register and purchase kits';
    }

    // Adjust button styles based on context
    if (showWithKits && showClassesOnly) {
      // Both buttons visible: kits is primary, classes-only is secondary
      withKitsBtn.classList.remove('button--secondary');
      withKitsBtn.classList.add('button--primary');
      classesOnlyBtn.classList.remove('button--primary');
      classesOnlyBtn.classList.add('button--secondary');
    } else if (showClassesOnly && !showWithKits) {
      // Only classes button visible: make it primary
      classesOnlyBtn.classList.remove('button--secondary');
      classesOnlyBtn.classList.add('button--primary');
    } else if (showWithKits && !showClassesOnly) {
      // Only kits button visible: make it primary
      withKitsBtn.classList.remove('button--secondary');
      withKitsBtn.classList.add('button--primary');
    }
  }

  /**
   * Sets the collapsed state of the summary panel (mobile only)
   * @param {boolean} collapsed - Whether to collapse the panel
   */
  setSummaryCollapsed(collapsed) {
    this.state.summaryCollapsed = collapsed;
    this.updateCollapsedState();
  }

  /**
   * Updates the visual collapsed/expanded state of the summary panel
   * @private
   */
  updateCollapsedState() {
    const { summaryContainer, collapsedBar, collapsedCount } = this.dom;

    if (!summaryContainer || !collapsedBar) return;

    const isCollapsed = this.state.summaryCollapsed;

    // Calculate total class count across all tickets
    const totalClasses = Object.values(this.state.selections).reduce(
      (sum, selections) => sum + selections.length,
      0
    );
    const totalKits = Object.keys(this.state.kits).length;

    // Update collapsed count text
    if (collapsedCount) {
      const parts = [];
      if (totalClasses > 0) {
        parts.push(`${totalClasses} class${totalClasses === 1 ? '' : 'es'}`);
      }
      if (totalKits > 0) {
        parts.push(`${totalKits} kit${totalKits === 1 ? '' : 's'}`);
      }
      collapsedCount.textContent = parts.length > 0 ? `${parts.join(', ')} selected` : 'No selections';
    }

    // Toggle visibility
    summaryContainer.hidden = isCollapsed;
    collapsedBar.hidden = !isCollapsed;
  }

  /**
   * Builds a sortable key so class lists render chronologically.
   * Missing or unparsable date/time sorts last.
   * @param {Object} classData - Class selection or registration
   * @returns {string} Sortable "YYYY-MM-DDHHMM" key
   * @private
   */
  getScheduleSortKey(classData) {
    const dateValue = classData.date || classData.date_formatted || '';
    const dateKey = /^\d{4}-\d{2}-\d{2}/.test(dateValue) ? dateValue.slice(0, 10) : '9999-99-99';

    const timeValue = classData.time_slot || classData.time || '';
    const match = /^(\d{1,2}):(\d{2})\s*(am|pm)$/i.exec(timeValue.trim());
    if (!match) return `${dateKey}9999`;

    let hours = Number(match[1]) % 12;
    if (match[3].toLowerCase() === 'pm') hours += 12;

    return `${dateKey}${String(hours).padStart(2, '0')}${match[2]}`;
  }

  /**
   * Returns a chronologically ordered copy, leaving stored state untouched
   * @param {Array} classes - Class selections or registrations
   * @returns {Array} New array in schedule order
   * @private
   */
  sortBySchedule(classes) {
    return [...classes].sort((a, b) => this.getScheduleSortKey(a).localeCompare(this.getScheduleSortKey(b)));
  }

  /**
   * Renders class selections into the summary panel
   * @param {HTMLElement} container - Container element to render into
   * @param {ClassSelection[]} selections - Array of class selections to render
   * @private
   */
  renderClassSelections(container, selections) {
    const orderedSelections = this.sortBySchedule(selections);
    const html = `
      <div class="registration-summary__ticket-group">
        <div class="registration-summary__ticket-header">
          <span class="registration-summary__ticket-name">Your Classes</span>
          <span class="registration-summary__ticket-count">${selections.length} selected</span>
        </div>
        <div class="registration-summary__classes-list">
          ${orderedSelections
            .map((selection) => {
              // Use stored class name instead of DOM lookup
              const className = selection.class_name || 'Class';
              const classTime = selection.time_slot || '';

              return `
                <div class="registration-summary__class-item">
                  <div class="registration-summary__class-info">
                    <span class="registration-summary__class-name">${escapeHTML(className)}</span>
                    <span class="registration-summary__class-meta">${escapeHTML(selection.date)} • ${escapeHTML(classTime)}</span>
                    <span class="registration-summary__class-meta">${escapeHTML(this.config?.messages?.roomLabel || 'Room:')} ${escapeHTML(getScheduleRoom(selection.class_id) || '—')}</span>
                  </div>
                  <button type="button" class="registration-summary__class-remove" data-remove-class="${selection.class_id}" aria-label="Remove ${escapeHTML(className)}">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  </button>
                </div>
              `;
            })
            .join('')}
        </div>
      </div>
    `;

    container.innerHTML = html;
  }

  /**
   * Renders kits into the summary panel
   * @param {HTMLElement} container - Container element to render into
   * @private
   */
  renderKits(container) {
    const kitsList = container.querySelector('[data-kits-list]');
    if (!kitsList) return;

    this.log('renderKits - state.kits:', JSON.stringify(this.state.kits, null, 2));

    const html = Object.entries(this.state.kits)
      .map(([kitVariantId, kitData]) => {
        const kitTitle = kitData.title || 'Kit';
        const kitPrice = kitData.priceFormatted || 'Price available at checkout';
        this.log('renderKits - rendering kit:', { kitVariantId, kitTitle, storedTitle: kitData.title, kitPrice });

        const checkedAttr = kitData.checked ? 'checked' : '';

        return `
          <div class="registration-summary__kit-item">
            <label class="registration-summary__kit-label">
              <input
                type="checkbox"
                class="registration-summary__kit-checkbox"
                data-kit-checkbox-toggle
                data-kit-variant-id="${kitVariantId}"
                ${checkedAttr}
              >
              <div class="registration-summary__kit-info">
                <span class="registration-summary__kit-name">${escapeHTML(kitTitle)}</span>
                <span class="registration-summary__kit-meta">${escapeHTML(kitPrice)} • Qty: ${escapeHTML(kitData.quantity)}</span>
              </div>
            </label>
          </div>
        `;
      })
      .join('');

    kitsList.innerHTML = html;
  }

  /**
   * Submits registration to the API and optionally adds kits to cart
   * Handles three flows: classes + kits, classes only, or kits only
   * @param {boolean} [purchaseKits=true] - Whether to add checked kits to cart
   * @returns {Promise<void>}
   */
  async submitRegistration(purchaseKits = true) {
    this.log('submitRegistration called, purchaseKits:', purchaseKits);

    if (!this.state.selectedTicketId) {
      NotificationModal.error('Please select a ticket');
      return;
    }

    const selections = this.state.selections[this.state.selectedTicketId] || [];
    const checkedKits = Object.entries(this.state.kits).filter(([_, kitData]) => kitData.checked);
    const hasKits = checkedKits.length > 0;
    const hasClasses = selections.length > 0;

    // Flow 3: Allow kits-only purchase (post-registration)
    // Flow 1 & 2: Require at least one class selection
    if (!hasClasses && !hasKits) {
      NotificationModal.error('Please select at least one class or kit to purchase');
      return;
    }

    if (!hasClasses && hasKits && !purchaseKits) {
      NotificationModal.error('Please select at least one class');
      return;
    }

    // Disable both buttons during submission
    const submitClassesOnlyBtn = document.querySelector('[data-submit-registration-classes-only]');
    const submitWithKitsBtn = document.querySelector('[data-submit-registration-with-kits]');

    if (submitClassesOnlyBtn) {
      submitClassesOnlyBtn.disabled = true;
      submitClassesOnlyBtn.textContent = 'Processing...';
    }
    if (submitWithKitsBtn) {
      submitWithKitsBtn.disabled = true;
      submitWithKitsBtn.textContent = 'Processing...';
    }

    try {
      let registrationResponse = null;
      let warnings = [];

      // Only submit class registration if there are classes selected
      // Flow 3 (kits-only) skips class registration
      if (hasClasses) {
        // Prepare registration data for API
        const registrationData = {
          ticket_id: this.state.selectedTicketId,
          operation: 'add',
          customer_id: this.customerId,
          customer_email: this.customerEmail,
          city: this.eventCity || this.normalizeCity(this.state.ticketConstraints.city),
          classes: selections.map((s) => ({
            class_id: s.class_id,
            class_name: s.class_name,
            date: s.date,
            time_slot: s.time_slot,
            kit_variant_id: s.kit_variant_id,
            kit_title: s.kit_title,
            kit_price: s.kit_price,
            kit_price_formatted: s.kit_price_formatted,
            registered_at: s.registered_at,
          })),
          kits: this.state.kits,
          registered_at: new Date().toISOString(),
        };

        // Send attendee_name only when we actually hold one. The key is an
        // update instruction on the backend, so sending '' from a tab whose
        // server sync has not landed would blank a saved name.
        const attendeeName = this.getAttendeeName(this.state.selectedTicketId);
        if (attendeeName) {
          registrationData.attendee_name = attendeeName;
        }

        // Submit class registration to API
        registrationResponse = await this.submitRegistrationToAPI(registrationData);
        this.log('Registration successful');

        // Update overview from API response (syncs this.tickets and dispatches event)
        this.updateOverviewFromResponse(registrationResponse, registrationResponse.recovered === true);
        if (!registrationResponse.recovered) await this.refreshCommittedSchedule();
        else this.updateAvailability();
        this.notifyScheduleChanged(this.state.selectedTicketId);

        // Clear the summary panel (pending selections) now that registration is committed
        // Keep ticket context so user can continue registering more classes
        this.clearTemporaryState(true);

        // Any backend warning attached to a successful mutation (kit_locked,
        // over_capacity, and anything added later) is surfaced with the rest of
        // the outcome below rather than special-cased here.
        warnings = this.collectRegistrationWarnings(registrationResponse);
        if (warnings.length > 0) {
          this.log('Registration completed with backend warnings:', warnings);
        }
      }

      if (purchaseKits && hasKits) {
        // User wants to purchase kits - show confirmation dialog first
        this.log('User selected', checkedKits.length, 'kit(s) for purchase, showing confirmation');

        // Show confirmation dialog - registration is complete, ask about kit purchase
        const confirmMessage = [
          this.registrationOutcomeText(
            registrationResponse,
            warnings,
            'Your registration is successful!'
          ),
          this.config?.messages?.kitConfirm ||
            'You can now continue to checkout with the kits.\n\nOnce you purchase a kit, you can no longer unregister from the associated class.',
        ].join(' ');

        const { confirmed, done } = await NotificationModal.confirm(confirmMessage, {
          confirmText: this.config?.messages?.kitConfirmContinue || 'Continue to Checkout',
          cancelText: this.config?.messages?.kitConfirmSkip || 'Skip Kit Purchase',
          loadingText: 'Adding kits to cart...',
          type: 'success'
        });

        if (!confirmed) {
          // User chose to skip kit purchase - registration is already complete
          done();
          this.log('User skipped kit purchase, reloading page');
          window.location.reload();
          return;
        }

        // Build cart items with line item properties for backend processing
        // Hidden properties (_ticket_id, _class_id) for backend, visible "Class Name" for customer
        const cartItems = [];
        for (const [kitVariantId, kitData] of checkedKits) {
          if (kitData.linkedClasses && kitData.linkedClasses.length > 0) {
            // Add one cart item per linked class with properties
            for (let i = 0; i < kitData.linkedClasses.length; i++) {
              const classId = kitData.linkedClasses[i];
              const className = kitData.linkedClassNames?.[i] || '';

              cartItems.push({
                id: kitVariantId,
                quantity: 1,
                properties: {
                  _ticket_id: this.state.selectedTicketId,
                  _class_id: classId,
                  _kit: 'true',
                  'Class Name': className,
                },
              });
              this.log('Adding kit to cart:', kitVariantId, 'for class:', classId);
            }
          } else if (kitData.quantity > 0) {
            // Fallback: no linked classes, just add with quantity
            cartItems.push({
              id: kitVariantId,
              quantity: kitData.quantity,
            });
            this.log('Adding kit to cart (no linked class):', kitVariantId, 'qty:', kitData.quantity);
          }
        }

        // Add kits to cart
        this.log('Adding', cartItems.length, 'items to cart');
        await this.addToCart(cartItems);

        // Store registration ID in cart for reference (if classes were registered)
        if (registrationResponse && registrationResponse.registration_id) {
          await this.updateCartAttributes({
            registration_id: registrationResponse.registration_id,
          });
        }

        // Close the confirmation modal and redirect to checkout
        done();
        window.location.href = '/checkout';
      } else {
        // No kits to purchase or user chose to register classes only
        this.log('Registration complete without kits');

        await NotificationModal.success(
          [
            this.registrationOutcomeText(
              registrationResponse,
              warnings,
              'Registration successful! You are registered for your selected classes.'
            ),
            hasKits && !purchaseKits ? 'You can purchase kits separately if needed.' : '',
          ]
            .filter(Boolean)
            .join(' ')
        );

        // Refresh page to show updated registration state
        window.location.reload();
      }
    } catch (error) {
      console.error('Registration failed:', error);
      if (error.code !== 'REGISTRATION_UNCONFIRMED') await this.refreshCommittedSchedule();
      NotificationModal.error(error.message || 'Failed to process registration. Please try again.');

      // Re-enable both buttons
      if (submitClassesOnlyBtn) {
        submitClassesOnlyBtn.disabled = false;
        submitClassesOnlyBtn.textContent = 'Register for classes only';
      }
      if (submitWithKitsBtn) {
        submitWithKitsBtn.disabled = false;
        submitWithKitsBtn.textContent = 'Register and purchase kits';
      }
    }
  }

  /**
   * Submits registration data to the backend API
   * Falls back to demo mode with fake success if API unavailable
   * @param {RegistrationData} registrationData - Registration data to submit
   * @returns {Promise<APIResponse>} API response or demo response
   * @private
   */
  async submitRegistrationToAPI(registrationData) {
    try {
      return await this.requestRegistration(registrationData);
    } catch (error) {
      if (error.isApiError || window.REGISTRATION_DEMO !== true) {
        throw error;
      }

      // Explicit demo mode only: return data shaped like a canonical response.
      // Return data structure matching real API so updateOverviewFromResponse works
      this.log('API not available, returning fake success:', error);

      const ticketRecord = this.findTicketById(registrationData.ticket_id);
      const existingClasses = ticketRecord?.selected_classes || [];
      const demoClasses = [...existingClasses];
      for (const selectedClass of registrationData.classes) {
        if (!demoClasses.some((entry) => entry.class_id === selectedClass.class_id)) {
          demoClasses.push(selectedClass);
        }
      }
      return {
        success: true,
        registration_id: `demo_reg_${Date.now()}`,
        message: 'Registration recorded (demo mode - no backend)',
        data: {
          ticket_id: registrationData.ticket_id,
          classes: demoClasses,
        },
      };
    }
  }

  /**
   * A disconnected response does not establish whether the write committed.
   * Check fresh canonical rows once; never replay a potentially committed write.
   */
  async requestRegistration(registrationData) {
    const subpath = window.REGISTRATION_DEV ? 'registration-dev' : 'registration';
    try {
      const response = await fetch(`/apps/${subpath}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registrationData),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const error = new Error(body.error?.message || body.message || 'Registration failed');
        error.code = body.error?.code;
        error.response = body;
        // A server/proxy failure may happen after the database commit.
        error.isApiError = true;
        error.isDefinitiveApiError = response.status >= 400 && response.status < 500;
        throw error;
      }
      // Await parsing so a truncated success response also takes the recovery path.
      return await response.json();
    } catch (error) {
      if (error.isDefinitiveApiError || window.REGISTRATION_DEMO === true) throw error;
      try {
        const response = await fetch(`/apps/${subpath}/data/registrations`, {
          method: 'GET',
          cache: 'no-store',
          signal: AbortSignal.timeout(10000),
          headers: { 'Content-Type': 'application/json' },
        });
        if (response.ok) {
          const result = await response.json();
          const ticket = result.success && result.data?.tickets?.find(
            (entry) => entry.ticket_id === registrationData.ticket_id
          );
          // Cache/metafield fallbacks can describe a stale schedule, even when
          // the GET succeeded. Only an explicitly row-backed snapshot proves it.
          if (ticket?.schedule_source === 'registration_rows' && Array.isArray(ticket.classes)) {
            const normalizeClassId = (id) => String(id ?? '').replace(/^gid:\/\/shopify\/Metaobject\//, '');
            const savedIds = new Set(ticket.classes.map((entry) => normalizeClassId(entry.class_id)));
            const confirmed = registrationData.operation === 'remove'
              ? !!registrationData.class_id && !savedIds.has(normalizeClassId(registrationData.class_id))
              : registrationData.operation === 'add' && registrationData.classes?.length > 0 &&
                registrationData.classes.every((entry) => savedIds.has(normalizeClassId(entry.class_id)));
            if (confirmed) {
              return {
                success: true,
                recovered: true,
                message: this.config?.messages?.saveRecovered ||
                  'Your requested class changes are on your saved schedule.',
                data: { ticket: { ...ticket, id: ticket.ticket_id } },
              };
            }
          }
        }
      } catch (verificationError) {
        this.log('Unable to confirm the saved schedule:', verificationError);
      }
      const uncertain = new Error(
        this.config?.messages?.saveUnconfirmed ||
        'We could not confirm your class changes. They may have saved. Refresh Tickets & Classes and check your schedule before trying again.'
      );
      uncertain.code = 'REGISTRATION_UNCONFIRMED';
      throw uncertain;
    }
  }

  /**
   * Builds the user-facing text for a successful mutation.
   *
   * The warning objects are the detailed form of what the backend's top-level
   * `message` summarises, so showing both repeats every count. Warnings win
   * when present because they also name the affected classes.
   * @param {Object|null} response - Successful registration response body
   * @param {string[]} warnings - Result of collectRegistrationWarnings
   * @param {string} fallback - Lead sentence when the backend sends no message
   * @returns {string} Text to display
   * @private
   */
  registrationOutcomeText(response, warnings, fallback) {
    if (response?.recovered) return response.message;
    if (warnings.length > 0) {
      return [fallback, ...warnings].join(' ');
    }
    return this.endSentence(response?.message || fallback);
  }

  /**
   * Terminates a server-supplied sentence so it reads correctly when other
   * sentences are appended after it.
   * @param {string} text - Sentence from the backend
   * @returns {string} The sentence with terminating punctuation
   * @private
   */
  endSentence(text) {
    return /[.!?]$/.test(text) ? text : `${text}.`;
  }

  /**
   * Collects backend warnings from a successful mutation response.
   *
   * The contract attaches them as top-level objects carrying their own
   * user-facing `message` plus the affected `classes` (`kit_locked`,
   * `over_capacity`, ...). Detecting that shape instead of naming each key
   * means a warning added on the backend is displayed without a theme change.
   * @param {Object} response - Successful registration response body
   * @returns {string[]} User-facing warning sentences
   * @private
   */
  collectRegistrationWarnings(response) {
    if (!response || typeof response !== 'object') return [];

    return Object.entries(response)
      .filter(
        ([key, value]) =>
          key !== 'data' &&
          key !== 'error' &&
          value &&
          typeof value === 'object' &&
          typeof value.message === 'string' &&
          Array.isArray(value.classes)
      )
      .map(([, value]) => {
        const names = value.classes
          .map((entry) => entry?.class_name || entry?.class_id)
          .filter(Boolean)
          .join(', ');
        return names ? `${value.message}: ${names}.` : `${value.message}.`;
      });
  }

  /**
   * Deregisters a class from a ticket
   * Sends a narrow remove operation; unrelated registrations are never inferred from omission.
   * @param {string} ticketId - Ticket ID
   * @param {string} classId - Class ID to remove
   * @param {string} date - Class date
   * @param {string} timeSlot - Class time slot
   * @returns {Promise<void>}
   */
  async deregisterClass(ticketId, classId, date, timeSlot) {
    this.log('deregisterClass:', classId);

    // Get the ticket record with current registrations
    const ticketRecord = this.findTicketById(ticketId);
    if (!ticketRecord) {
      throw new Error('Ticket not found');
    }

    const currentClasses = ticketRecord.selected_classes || [];

    // Find the class being removed to check for associated kit
    const classToRemove = currentClasses.find(cls =>
      cls.class_id === classId && cls.date === date && cls.time_slot === timeSlot
    );

    // Filter out the class to remove
    const remainingClasses = currentClasses.filter(cls =>
      !(cls.class_id === classId && cls.date === date && cls.time_slot === timeSlot)
    );

    // If nothing changed, the class wasn't found
    if (remainingClasses.length === currentClasses.length) {
      throw new Error('Class not found in registrations');
    }

    // Send only the explicit removal intent, with the same response recovery as adds.
    try {
      const result = await this.requestRegistration({
        ticket_id: ticketId,
        operation: 'remove',
        class_id: classId,
        customer_id: this.customerId,
        customer_email: this.customerEmail,
        city: this.eventCity || ticketRecord.city,
        kits: {}, // No kit changes during deregistration
        registered_at: new Date().toISOString(),
      });

      // Update the overview table with the new ticket data
      if (result.data) {
        this.updateOverviewFromResponse(result, result.recovered === true);
        // Use API response as source of truth for selected_classes
        // Support both new structure (data.ticket.classes) and old structure (data.classes)
        const ticketData = result.data.ticket || result.data;
        ticketRecord.selected_classes = ticketData.classes || [];
      } else {
        // Fallback if no data in response
        ticketRecord.selected_classes = remainingClasses;
      }

      if (!result.recovered) await this.refreshCommittedSchedule();
      this.notifyScheduleChanged(ticketId);

      // Update availability in the class grid
      this.updateAvailability();

      // Report whatever the backend flagged; kit locks additionally mean the
      // class stayed registered, so its kit must stay in the cart.
      const warnings = this.collectRegistrationWarnings(result);
      if (result.kit_locked?.classes?.length > 0) {
        NotificationModal.warning(
          warnings.join(' ') || 'Could not remove the class because its kit has been purchased.'
        );
      } else {
        if (classToRemove?.kit_variant_id) {
          await this.removeKitFromCart(classToRemove.kit_variant_id, ticketId, classId);
        }
        if (warnings.length > 0) {
          NotificationModal.warning(warnings.join(' '));
        } else {
          // The backend's message narrates the resulting schedule ("Successfully
          // registered for N classes"), which reads wrong after a removal.
          NotificationModal.success('Class removed from your registration.');
        }
      }

    } catch (error) {
      // Check if it's a network error (API unavailable) - use demo mode
      if (window.REGISTRATION_DEMO === true && (error.name === 'TypeError' || error.message.includes('fetch'))) {
        this.log('API unavailable, using demo mode for deregistration');

        // Update ticket data in memory (this.tickets is single source of truth)
        ticketRecord.selected_classes = remainingClasses;

        // Dispatch event to update overview
        const event = new CustomEvent('registration:updated', {
          detail: {
            ticket: {
              id: ticketId,
              selected_classes: remainingClasses,
            },
          },
        });
        document.dispatchEvent(event);

        // Update availability in the class grid
        this.updateAvailability();

        // Show success message
        NotificationModal.success('Class removed from your registration (demo mode).');
        return;
      }

      if (error.code !== 'REGISTRATION_UNCONFIRMED') await this.refreshCommittedSchedule();
      throw error; // Re-throw so the caller can handle it
    }
  }

  /**
   * Adds items to the Shopify cart via AJAX API
   * Falls back to fake success if API unavailable
   * @param {CartItem[]} items - Array of items to add to cart
   * @returns {Promise<Object>} Cart response or fake success
   * @private
   */
  async addToCart(items) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Failed to add items to cart');
      }

      return response.json();
    } catch (error) {
      // Cart API failed - return fake success for demo
      this.log('Cart API not available, returning fake success:', error);
      return {
        success: true,
        items: items,
      };
    }
  }

  /**
   * Removes a kit from the Shopify cart when deregistering a class
   * Finds cart items matching the kit variant and ticket/class properties
   * @param {string} kitVariantId - Kit variant ID to remove
   * @param {string} ticketId - Ticket ID to match in line item properties
   * @param {string} classId - Class ID to match in line item properties
   * @returns {Promise<void>}
   * @private
   */
  async removeKitFromCart(kitVariantId, ticketId, classId) {
    try {
      // Get current cart contents
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        return;
      }

      const cart = await cartResponse.json();

      // Find cart items that match this kit and class
      const itemsToRemove = cart.items.filter(item => {
        // Match by variant ID
        if (String(item.variant_id) !== String(kitVariantId)) {
          return false;
        }

        // Check line item properties for _ticket_id and _class_id (hidden properties)
        const props = item.properties || {};
        const itemTicketId = props._ticket_id;
        const itemClassId = props._class_id;

        // If properties exist, they must match
        if (itemTicketId && itemTicketId !== ticketId) {
          return false;
        }
        if (itemClassId && itemClassId !== classId) {
          return false;
        }

        // Match found - either properties match or no properties (standalone kit)
        return true;
      });

      if (itemsToRemove.length === 0) {
        return;
      }

      // Remove each matching item by setting quantity to 0
      for (const item of itemsToRemove) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: item.key,
            quantity: 0,
          }),
        });
      }
    } catch (error) {
      // Cart operations failed - don't block deregistration
      this.log('Could not remove kit from cart:', error.message);
    }
  }

  /**
   * Updates cart attributes via Shopify AJAX API
   * Used to store registration ID in cart for reference
   * @param {Object<string, string>} attributes - Key-value pairs to set on cart
   * @returns {Promise<Object>} Cart response or fake success
   * @private
   */
  async updateCartAttributes(attributes) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attributes }),
      });

      if (!response.ok) {
        throw new Error('Failed to update cart attributes');
      }

      return response.json();
    } catch (error) {
      // Cart update failed - return fake success for demo
      this.log('Cart update API not available, returning fake success:', error);
      return {
        success: true,
        attributes: attributes,
      };
    }
  }
}

// Initialize on DOM ready and expose globally for kit purchases from registered classes
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.classRegistrationManager = new ClassRegistrationManager();
  });
} else {
  window.classRegistrationManager = new ClassRegistrationManager();
}

window.REGISTRATION_DEV = false;
